import { Provider, Signal } from "@angular/core";
import {
    ATOM_RT_BOLD_ACTION_ARIA_LABEL,
    ATOM_RT_EMOJI_ACTION_ARIA_LABEL,
    ATOM_RT_EMOJI_I18N,
    ATOM_RT_ITALIC_ACTION_ARIA_LABEL,
    ATOM_RT_STRIKE_ACTION_ARIA_LABEL,
    ATOM_RT_VARIABLES_ACTION_ARIA_LABEL,
    ATOM_RT_VARIABLE_UNKNOWN_LABEL,
    type AtomRtEmojiI18n,
} from "@atomchat-io/ui-design-system";
import { translateObjectSignal, translateSignal } from "@jsverse/transloco";

const BASE_KEY_PATH = "atom_rich_text";
/**
 * Localizes `atom-rich-text` toolbar action aria-labels, the unknown-variable
 * chip copy and the emoji picker's own chrome via the DS injection tokens.
 *
 * Tokens carry a `Signal` so labels stay in sync on live language switches
 * (`reRenderOnLangChange: true`).
 */
export const provideAtomRichTextLabels = (): Provider[] => [
    {
        provide: ATOM_RT_BOLD_ACTION_ARIA_LABEL,
        useFactory: (): Signal<string> =>
            translateSignal(`${BASE_KEY_PATH}.bold_action_aria_label`),
    },
    {
        provide: ATOM_RT_ITALIC_ACTION_ARIA_LABEL,
        useFactory: (): Signal<string> =>
            translateSignal(`${BASE_KEY_PATH}.italic_action_aria_label`),
    },
    {
        provide: ATOM_RT_STRIKE_ACTION_ARIA_LABEL,
        useFactory: (): Signal<string> =>
            translateSignal(`${BASE_KEY_PATH}.strike_action_aria_label`),
    },
    {
        provide: ATOM_RT_VARIABLES_ACTION_ARIA_LABEL,
        useFactory: (): Signal<string> =>
            translateSignal(`${BASE_KEY_PATH}.variables_action_aria_label`),
    },
    {
        provide: ATOM_RT_EMOJI_ACTION_ARIA_LABEL,
        useFactory: (): Signal<string> =>
            translateSignal(`${BASE_KEY_PATH}.emoji_action_aria_label`),
    },
    {
        provide: ATOM_RT_VARIABLE_UNKNOWN_LABEL,
        useFactory: (): Signal<string> =>
            translateSignal(`${BASE_KEY_PATH}.variable_unknown_label`),
    },
    {
        // The picker's chrome is an OBJECT (~21 strings across `categories` and
        // `skintones`), so it resolves with `translateObjectSignal` in a single
        // call instead of one `translateSignal` per key. Its initial value is
        // `{}` while the bundle loads, which the DS reads as "nothing provided"
        // and falls back to its English defaults — no blank labels.
        provide: ATOM_RT_EMOJI_I18N,
        useFactory: (): Signal<AtomRtEmojiI18n> =>
            translateObjectSignal(`${BASE_KEY_PATH}.emoji_picker`),
    },
];
